<template>
  <div class="editor-tool-bar">
    <div class="title-bar">
      <button class="btn-back" @click="handleBack">
        <SvgIcon name="editor-back" :size="18" />
        <span>{{ $t('editor.back') }}</span>
      </button>
      <span class="divider"></span>
      <input
        v-if="isEditingTitle"
        ref="titleInputRef"
        v-model="editTitleValue"
        class="project-title-input"
        maxlength="50"
        @keydown.enter="finishEditTitle"
        @keydown.escape="cancelEditTitle"
        @blur="finishEditTitle"
      />
      <span
        v-else
        class="project-title"
        :title="displayTitle !== projectStore.name ? projectStore.name : '双击编辑名称'"
        @dblclick="startEditTitle"
        >{{ displayTitle
        }}<span v-if="projectStore.isModify" class="modify-indicator">*</span></span
      >
    </div>
    <div class="operation-bar">
      <button v-if="false" class="btn-secondary" @click="handlePlay">
        <SvgIcon name="editor-share" :size="22" />
        <span>播放</span>
      </button>
      <button class="btn-secondary" @click="openSharePanel">
        <SvgIcon name="editor-share" :size="22" />
        <span>{{ $t('editor.share') }}</span>
      </button>
      <button class="btn-secondary" @click="showExportDialog = true">
        <SvgIcon name="editor-export-pdf" :size="22" />
        <span>{{ $t('editor.export') }}</span>
      </button>
      <button class="btn-secondary" @click="handlePreview">
        <SvgIcon name="editor-preview" :size="22" />
        <span>{{ $t('editor.preview') }}</span>
      </button>
      <button class="btn-save" @click="handleSave">
        <SvgIcon name="editor-save" :size="22" color="#ffffff" />
        <span>{{ $t('editor.save') }}</span>
      </button>
    </div>
    <!-- 导出弹窗 -->
    <ExportDialog v-model="showExportDialog" />
    <!-- 导出进度弹窗 -->
    <ExportProgressDialog />
    <!-- 分享面板 -->
    <BookSharePanel
      :visible="shareVisible"
      :title="projectStore.name"
      :anchor-rect="shareAnchorRect"
      @close="shareVisible = false"
    />
    <!-- 预览弹窗 -->
    <EditorPreviewModal :visible="previewVisible" @close="previewVisible = false" />
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import ExportDialog from './tool/ExportDialog.vue';
  import ExportProgressDialog from './tool/ExportProgressDialog.vue';
  import BookSharePanel from '@/views/book/components/BookSharePanel.vue';
  import EditorPreviewModal from './EditorPreviewModal.vue';
  import { messageBoxManager, projectManager } from '@/managers';
  import { useProjectStore } from '@/store';
  import { useRouter, useRoute } from 'vue-router';
  import { emitter } from '@/utils';
  import { EVENT_NAMES } from '@/constants';

  const projectStore = useProjectStore();
  const router = useRouter();
  const route = useRoute();

  const showExportDialog = ref(false);
  const shareVisible = ref(false);
  const shareAnchorRect = ref<DOMRect | null>(null);
  const previewVisible = ref(false);

  /** 打开分享面板，先保存项目，再记录分享按钮位置供面板定位。 */
  async function openSharePanel(e: MouseEvent) {
    await projectManager.save();
    shareAnchorRect.value = (e.currentTarget as HTMLElement).getBoundingClientRect();
    shareVisible.value = true;
  }

  // ===== 双击编辑标题 =====
  const isEditingTitle = ref(false);
  const editTitleValue = ref('');
  const titleInputRef = ref<HTMLInputElement | null>(null);

  const MAX_TITLE_DISPLAY = 20;
  const displayTitle = computed(() => {
    const name = projectStore.name;
    return name.length > MAX_TITLE_DISPLAY ? name.slice(0, MAX_TITLE_DISPLAY) + '…' : name;
  });

  function startEditTitle() {
    editTitleValue.value = projectStore.name;
    isEditingTitle.value = true;
    nextTick(() => {
      titleInputRef.value?.focus();
      titleInputRef.value?.select();
    });
  }

  function finishEditTitle() {
    if (!isEditingTitle.value) return;
    const trimmed = editTitleValue.value.trim();
    if (trimmed && trimmed !== projectStore.name) {
      projectStore.updateProjectName(trimmed);
    }
    isEditingTitle.value = false;
  }

  function cancelEditTitle() {
    isEditingTitle.value = false;
  }

  // 从故事列表页面点击"导出PDF"跳转过来时，等待项目加载完成后自动弹出导出弹窗
  watch(
    () => projectStore.isLoading,
    (loading, prevLoading) => {
      if (!loading && prevLoading && route.query.exportPdf === 'true') {
        showExportDialog.value = true;
        router.replace({ query: { ...route.query, exportPdf: undefined } });
      }
    }
  );

  function handlePlay() {
    emitter.emit(EVENT_NAMES.TEST, { value: 1 });
  }

  async function handleBack() {
    if (projectStore.isModify) {
      const confirmed = await messageBoxManager.confirm(
        '当前项目有未保存的更改，是否保存后再返回？',
        '提示'
      );
      if (confirmed) {
        await projectManager.save();
      }
    }
    router.push({ name: 'story' });
  }

  async function handlePreview() {
    await projectManager.save();
    previewVisible.value = true;
  }

  function handleSave() {
    projectManager.save();
  }
</script>

<style scoped>
  .editor-tool-bar {
    box-sizing: border-box;
    width: 100%;
    height: 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 24px;
    background: #ffffff;
    border-bottom: 1px solid #f1f5f9;
    box-shadow:
      0px 2px 8px 0px rgba(0, 0, 0, 0.04),
      0px 1px 0px 0px rgba(241, 245, 249, 1);
  }

  /* ===== 左侧: 标题区域 ===== */

  .title-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }

  .btn-back {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border: none;
    border-radius: 10px;
    background: transparent;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    color: #333333;
  }

  .btn-back:hover {
    background: #f1f5f9;
  }

  .divider {
    display: block;
    width: 1px;
    height: 20px;
    background: #e2e8f0;
    border-radius: 9999px;
  }

  .project-title {
    font-size: 17px;
    font-weight: 500;
    line-height: 25.5px;
    letter-spacing: -0.025em;
    color: #333333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: default;
    border-radius: 6px;
    padding: 2px 6px;
    margin: -2px -6px;
    transition: background 0.15s;
  }

  .project-title:hover {
    background: #f1f5f9;
  }

  .modify-indicator {
    color: #ef4444;
    margin-left: 2px;
    font-weight: 700;
  }

  .project-title-input {
    font-family: inherit;
    font-size: 17px;
    font-weight: 500;
    line-height: 25.5px;
    letter-spacing: -0.025em;
    color: #333333;
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 2px 6px;
    margin: -2px -6px;
    outline: none;
    min-width: 120px;
  }

  .project-title-input:focus {
    border-color: #6033e1;
    background: #ffffff;
    box-shadow: 0 0 0 2px rgba(96, 51, 225, 0.15);
  }

  /* ===== 右侧: 操作按钮区域 ===== */

  .operation-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  /* 次要按钮: 分享 / 导出PDF / 预览 */
  .btn-secondary {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
    background: transparent;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    line-height: 19.5px;
    color: #475569;
  }

  .btn-secondary:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  /* 主要按钮: 保存 */
  .btn-save {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 16px;
    border: none;
    border-radius: 9px;
    background: #6033e1;
    box-shadow: 0px 2px 10px 0px rgba(96, 51, 225, 0.35);
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    line-height: 19.5px;
    color: #ffffff;
    flex-shrink: 0;
  }

  .btn-save:hover {
    background: #7c5cec;
  }
</style>
